import React, { useEffect, useState } from 'react';
import { useReactiveVar, useQuery } from '@apollo/client';

import PageHeader from 'components/PageHeader';
import StripeIntegration from './components/StripeIntegration';
import ZapierIntegration from './components/ZapierIntegration';
import QuickbooksIntegration from './components/QuickbooksIntegration';
import IntegromatIntegration from './components/IntegromatIntegration';
import FreshbooksIntegration from './components/FreshbooksIntegration';
// import XeroIntegration from './components/XeroIntegration';
import SlackIntegration from './components/SlackIntegration';
import CodeIntegration from './components/CodeIntegration';
import UserwayIntegration from './components/UserwayIntegration';
import WixIntegration from './components/WixIntegration';
import MorningIntegration from './components/MorningIntegration';
import MondayIntegration from './components/MondayIntegration';
import PipedriveIntegration from './components/PipedriveIntegration';
import HubSpotIntegration from './components/HubspotIntegration';
import Loader from 'components/Loader';

import { getUserId, userVar } from 'graphql/cache';
import { USER_DETAILS } from 'graphql/queries/userQueries';

import './Integration.scss';
import { ChatwayIntegration } from './components/ChatwayIntegration';
import { Button, Tabs } from 'antd';
import { WebhookListing } from './components/webhooks';
import SquareIntegration from './components/SquareIntegration';

const { TabPane } = Tabs;

const Integration = () => {
  let user = useReactiveVar(userVar);
  const [activeTab, setActiveTab] = useState('1');
  const [webhookModal, setWebhookModal] = useState(false);

  const { data: userInfo, refetch: refetchUser } = useQuery(USER_DETAILS, {
    variables: { id: getUserId() },
    skip: user || !getUserId(),
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (res) => {
      const { fetchUser } = res;
      userVar({ ...user, ...fetchUser });
    },
  });

  useEffect(() => {
    document.title = 'Prospero - Integrations';
  });

  const handleRefetchUser = async () => {
    try {
      const {
        data: { fetchUser },
      } = await refetchUser();
      return userVar({ ...user, ...fetchUser });
    } catch (error) {
      console.error(error);
    }
  };

  const onTabChange = (key) => {
    setActiveTab(key);
  };

  user = user || userInfo?.fetchUser;
  const isDisabled = user?.paymentStatus?.canLock || !!user?.teamId;

  return !user ? (
    <Loader />
  ) : (
    <div className="container">
      <div className="integration">
        <div className="integration-header">
          <PageHeader pageTitle="Integrations" />
        </div>
        <Tabs
          activeKey={activeTab}
          onChange={onTabChange}
          tabBarExtraContent={
            activeTab === '2' && (
              <Button
                disabled={isDisabled}
                className="large-btn secondary-btn"
                style={{ marginLeft: 'auto' }}
                onClick={() => setWebhookModal({ isEdit: false, data: { encrypted: false } })}>
                New Webhook
              </Button>
            )
          }>
          <TabPane tab="List" key="1">
            <div className="integration-main-content">
              <div className="integration-subsection" style={{ marginTop: '0px' }}>
                <span>Payments</span>
              </div>
              <StripeIntegration
                data={user.stripe || {}}
                userEmail={user.emails?.[0]?.address}
                userRole={user?.roles && user?.roles[0]}
                handleRefetchUser={handleRefetchUser}
              />
              <SquareIntegration
                data={!!user.square || false}
                userEmail={user.emails?.[0]?.address}
                userRole={user?.roles && user?.roles[0]}
                handleRefetchUser={handleRefetchUser}
              />
              <div className="integration-subsection">
                <span>Automations</span>
              </div>
              <ZapierIntegration data={user.zaps} userRole={user?.roles && user?.roles[0]} />
              <IntegromatIntegration
                data={user.integromatHooks}
                userVar={userVar}
                handleRefetchUser={handleRefetchUser}
                userRole={user?.roles && user?.roles[0]}
              />
              <div className="integration-subsection">
                <span>Invoices</span>
              </div>
              <QuickbooksIntegration
                data={user.quickbooks}
                userRole={user?.roles && user?.roles[0]}
                refetchUser={refetchUser}
                user={user}
              />
              <FreshbooksIntegration
                data={user.freshbooks}
                userRole={user?.roles && user?.roles[0]}
                refetchUser={refetchUser}
                user={user}
              />
              {/* {user?.xero?.connected && (
                <XeroIntegration
                  data={user.xero}
                  userRole={user?.roles && user?.roles[0]}
                  refetchUser={handleRefetchUser}
                  user={user}
                />
              )} */}
              <MorningIntegration
                data={user.morninginvoice}
                userRole={user?.roles && user?.roles[0]}
                handleRefetchUser={handleRefetchUser}
                user={user}
              />
              <div className="integration-subsection">
                <span>CRM</span>
              </div>
              <HubSpotIntegration
                data={user.hubspot}
                userRole={user?.roles && user?.roles[0]}
                refetchUser={handleRefetchUser}
                user={user}
              />
              <PipedriveIntegration
                data={user.pipedrive}
                userRole={user?.roles && user?.roles[0]}
                refetchUser={refetchUser}
                user={user}
              />
              <MondayIntegration
                data={user.monday}
                userRole={user?.roles && user?.roles[0]}
                refetchUser={handleRefetchUser}
                user={user}
              />
              <div className="integration-subsection">
                <span>Other</span>
              </div>
              <ChatwayIntegration
                data={user.chatwayScripts || []}
                userEmail={user.emails?.[0]?.address}
                userRole={user?.roles && user?.roles[0]}
                handleRefetchUser={handleRefetchUser}
              />
              {!user?.paymentStatus?.canLock && user?.profile?.oldWixUser && (
                <WixIntegration
                  data={user?.wix}
                  userRole={user?.roles && user?.roles[0]}
                  handleRefetchUser={handleRefetchUser}
                />
              )}
              <SlackIntegration
                data={user.slack}
                userRole={user?.roles && user?.roles[0]}
                refetchUser={refetchUser}
                user={user}
              />
              <UserwayIntegration data={user?.userway || false} />
              <CodeIntegration data={user.scripts || []} userRole={user?.roles && user?.roles[0]} />
            </div>
          </TabPane>
          <TabPane tab="Webhooks" key="2">
            {activeTab === '2' && (
              <WebhookListing setWebhookModal={setWebhookModal} webhookModal={webhookModal} />
            )}
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
};

export default Integration;
